import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { Sidebar } from 'primereact/sidebar';

import googleChatIcon from "../../images/google_chat_icon.png"

import "./index.css"

// inputs
import { Button, InputSwitchField } from '../../helpers';
import Table from '../../helpers/Table';

import Loading from '../../common/Loading'
import ButtonGroup from '../../common/ButtonGroup';
import PMCompanyName from '../../common/PMCompanyName';

// utils
import { getCompanyShortName, getFullName, isMobile } from "../../utils";

// components
import Search from './components/Search'

// redux
import { fetchUsersList, updateListUserData, setIsSearchVisible, setIsSearchPinned  } from "../../services/redux/reducers/listUser/listUser";
import { raiseToast } from '../../services/redux/reducers/toast/toast';

// apis
import { updateUserStatusById } from '../../services/api/user';
import { getGoogleUserChatUri } from '../../services/api/googleChat';
import useTranslationWithLoading from '../../common/hooks/useTranslationWIthLoading';
import { useSearchPin } from '../../hooks/useSearchPin';


const ListUser = (props) => {

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { isSearchPinned, toggleSearchPin, setSearchPin } = useSearchPin()

    const [isLoading, setIsLoading] = useState(false)
    const [isHoveringSearchZone, setIsHoveringSearchZone] = useState(false)
    const [hasEverHoveredRightEdge, setHasEverHoveredRightEdge] = useState(false)

    const listUserData = useSelector(state => state.users.listUserData)
    const isPageLoading = useSelector(state => state.users.loading)
    const paginationParams = useSelector(state => state.users.paginationParams)
    const searchParams = useSelector(state => state.users.searchParams)
    const isSearchVisible = useSelector(state => state.users.isSearchVisible)
    const isSearchActive = useSelector(state => state.users.isSearchActive)
    const userDetails = useSelector((state) => state.userDetails)

    useEffect(() => {
        if (!isSearchPinned && !isHoveringSearchZone) {
            dispatch(setIsSearchVisible(false))
          }

        const handleMouseMove = (e) => {
            if (isSearchPinned) return

            const panelWidth = window.innerWidth * 0.18
            const buffer = 8
            const screenRightEdge = window.innerWidth
            const activeZoneLeft = screenRightEdge - (panelWidth + buffer)

            if (e.clientX < activeZoneLeft) {
                setIsHoveringSearchZone(false)
                setHasEverHoveredRightEdge(false)
            } else {
                setIsHoveringSearchZone(true)
            }
        }

        if (!isSearchPinned && (isHoveringSearchZone || hasEverHoveredRightEdge)) {
            window.addEventListener('mousemove', handleMouseMove)
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [isSearchPinned, isHoveringSearchZone, hasEverHoveredRightEdge])

    const isMobileDevice = isMobile()

    const incrementPageNo = async (e) => {
        let paginatorParams = {
            paginator: true,
            pageNo: e.page + 1,
            recordsPerPage: e.rows,
            limitedPagination: true,
            getCount: false,
            ...searchParams
        }

        if(!searchParams.searchBy || searchParams.searchBy !== 'all'){
            paginatorParams[searchParams.searchBy] = searchParams.searchText.trim()
        }

        dispatch(fetchUsersList({...paginatorParams }))
    }

    const handleUpdateUserStatus = async (row) => {
        if(row['statusId'] === 1){
            if(window.confirm("Do u want to deactivate this user")){
                setIsLoading(true)
                const updatedResponse = await updateUserStatusById(row['userId'], {status: 2})
                setIsLoading(false)
                if(updatedResponse.failure){
                    return dispatch(raiseToast({severity:'error', detail: "User can't found", life: 3000})) 
                }else{
                    const updatedList = listUserData.map((eachUser) => {
                        if(eachUser.userId === row['userId']){
                            return {
                                ...eachUser,
                                "statusId": 2
                            }
                        }else{
                            return {
                                ...eachUser
                            }
                        }
                    })

                    dispatch(updateListUserData(updatedList))
                    dispatch(raiseToast({severity:'success', detail: "Updated user status", life: 3000}))
                }
            }
        }else{
            if(window.confirm("Do u want to activate this user")){
                setIsLoading(true)
                const updatedResponse = await updateUserStatusById(row['userId'], {status: 1})
                setIsLoading(false)
                if(updatedResponse.failure){
                    return dispatch(raiseToast({severity:'error', detail: "User can't found", life: 3000})) 
                }else{
                    const updatedList = listUserData.map((eachUser) => {
                        if(eachUser.userId === row['userId']){
                            return {
                                ...eachUser,
                                statusId: 1
                            }
                        }else{
                            return {
                                ...eachUser
                            }
                        }
                    })

                    dispatch(updateListUserData(updatedList))
                    dispatch(raiseToast({severity:'success', detail: "Updated user status", life: 3000}))
                }
            }
        }
    }

    const handleSearchTabDisplay = () => {
        if (!isSearchPinned) {
          dispatch(setIsSearchVisible(!isSearchVisible))
        }
    }

    const handleGetGoogleUserChatUri = async (approvalEmail) => {
        if (!approvalEmail) {
            return dispatch(raiseToast({ severity: 'error', detail: "Approval user not found. Please contact tech team", life: 3000 }))
        }
        let data = {
            userId: userDetails.userId,
            emailId: approvalEmail
        }

        setIsLoading(true)
        const getGoogleUserChatUriRes = await getGoogleUserChatUri(data)
        setIsLoading(false)

        if (getGoogleUserChatUriRes.failure) {
            if (window.confirm("Chat with the user doesn't exist.\nPlease open chat manually, and send the message.")) {
                window.open("https://mail.google.com/mail/u/0/?tab=rm&ogbl#chat/home", '_blank')
            }
        } else {
            window.open(getGoogleUserChatUriRes.data, '_blank')
        }
    }

    const getStatusBody = (row) =>{
        return(
            <InputSwitchField  permissions={[31,32]} onChange={() => handleUpdateUserStatus(row)} checked={row['statusId'] === 1 ? true : false}/> 
        )
    }
    
    const emailIdBody  = (row) => {
        if(row.emailId){
            return(
                <div className='flex justify-content-center align-items-center'>
                    <p onClick={() => navigate(`/editUser/${row.userId}`)} style={{fontSize : "13px", textDecoration: "underline", color: "#3366cc", cursor: "pointer", padding: "0px", margin: "0px"}}>{row["emailId"]}</p>
                    <a style={{ cursor: "pointer" }} onClick={() => handleGetGoogleUserChatUri(row.emailId)}>
                        <img
                            className="ml-1 mb-0 mt-0"
                            src={googleChatIcon}
                            style={{ height: 13 }}
                            alt="Google Chat"
                        />
                    </a>
                </div>
            )
        }

        return "-"
    }

    const phoneNumberBody = (row) => {
        if(row.phoneNumber){
            return(
                <div className='flex justify-content-center align-items-center'>
                    <p style={{cursor: "pointer", padding: "0px", margin: "0px"}}>{row.phoneNumber}</p>
                    <div style={{ cursor: 'pointer' }} onClick={() => window.open(`https://wa.me/${row.phoneNumber}`)}>
                        <i className="pi pi-whatsapp pl-1 doc-copy-icon" style={{ color: "#00baaa" }}></i>
                    </div>
                </div>
            )
        }

        return "-"
    }

    const getCompanyName = (row) =>{
        let companies = []
        let companyIds = []
        
        if(row.userRoles && row.userRoles?.length > 0){
            row.userRoles?.map(eachRole => {
                if(!companyIds.includes(eachRole.companyId)){
                    if(eachRole?.company?.companyName){
                        let companyShortName = getCompanyShortName(eachRole?.company?.companyName)
                        if (companyShortName) {
                            companies.push(
                                <PMCompanyName key={eachRole.companyId} companyName={companyShortName} companyId={eachRole.companyId} />
                            )
                        }
                    }
                }
                companyIds.push(eachRole.companyId)
            })
        }

        if (companies.length > 0) {
            return companies
        }

        return "-"
    }

    const userNameBody = (row) => {
        return getFullName(row.firstName, row.lastName)
    }

    const tableColumns = [
        {
            header: 'UserName',
            field: "",
            style:{ minWidth: "180px"},
            body: userNameBody
        },
        {
            header: 'Email ID',
            field: '',
            body: emailIdBody
        },
        {
            header: 'Phone No',
            field: 'phoneNumber',
            body: phoneNumberBody,
            style:{ minWidth: "150px"}
        },
        {
            header: 'Company Name',
            field: 'company.companyName',
            body: getCompanyName,
            style:{ minWidth: "250px"}
        },
        {
            header: 'Status',
            field: '',
            body: getStatusBody
        }
    ]
    
    const actionButtons = [
        {
            icon: "pi pi-filter",
            label: "Filter",
            command: () => handleSearchTabDisplay(),
            className: isSearchActive ? "active-group-button" : ""
        },
        {
            icon: isSearchPinned ? "pi pi-lock" : "pi pi-lock-open",
            command: () => toggleSearchPin() 
        }
    ]

    let displaySearchBox = isSearchPinned || isHoveringSearchZone || isSearchVisible

    return (
        <>
            <div className="user-layout flex flex-column">
                <div
                    onMouseEnter={() => {
                        if (!isSearchPinned) {
                            setIsHoveringSearchZone(true)
                            setHasEverHoveredRightEdge(true)
                            dispatch(setIsSearchVisible(true))
                        }
                    }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        right: 0,
                        width: '8px',
                        height: '100vh',
                        zIndex: 999
                    }}
                />
                <div className="flex flex-column md:flex-row md:justify-content-between">
                    <h4 className="mx-2 my-1 md:m-2">USERS</h4>
                    <div className="flex gap-2 justify-content-end mb-2">
                        {/* <Button icon="pi pi-filter" className={`h-2rem p-2 action-btn hover:surface-400 hover:text-700 surface-300 ${isSearchActive ? 'search-filter-active' : ''}`} label="Filter" onClick={handleSearchTabDisplay} /> */}
                        <ButtonGroup buttons={actionButtons} />
                    </div>
                </div>
                <div className='flex flex-row'>
                    <div className="user-table" style={{ order: "1", width: displaySearchBox && !isMobileDevice ? "82%" : "100%", maxHeight: "'calc(100%-100px)'" }}>
                        <div className='p-card mb-0'>
                            <div className="flex-grow-1" >
                                <Table
                                    lazy
                                    page={paginationParams.pageNo}
                                    onPageChange={(e) => incrementPageNo(e)}
                                    tableColumns={tableColumns}
                                    data={listUserData}
                                    rows={paginationParams.recordsPerPage}
                                    scrollable={"true"}
                                    scrollHeight="550px"
                                    tableClassName=""
                                    paginator
                                    totalRecords={paginationParams.totalRecords}
                                    excessPagesFound= {paginationParams.excessPagesFound}
                                    loading={isPageLoading}
                                />
                            </div>
                        </div>
                    </div>

                    {displaySearchBox && !isMobileDevice && (
                        <div
                            style={{ order: "2", width: "18%", paddingLeft: "10px" }} className="justify-content-end"
                            onMouseLeave={(e) => {
                                const bounds = e.currentTarget.getBoundingClientRect()
                                const leftExit = e.clientX < bounds.left
                                const topExit = e.clientY < bounds.top
                                const bottomExit = e.clientY > bounds.bottom

                                if (!isSearchPinned && (leftExit || topExit || bottomExit)) {
                                    setIsHoveringSearchZone(false)
                                    setHasEverHoveredRightEdge(false)
                                }
                            }}

                            onMouseEnter={() => {
                                if (!isSearchPinned) {
                                    setIsHoveringSearchZone(true)
                                }
                            }}
                        >
                            <Search />
                        </div>
                    )}

                    {   
                        isSearchVisible && isMobileDevice &&
                            <div style={{ order: "2", width: "18%", paddingLeft: "10px"}} className="justify-content-end ">
                                <Sidebar position='right' visible={isSearchVisible} onHide={() => dispatch(setIsSearchVisible(false))}>
                                    <Search />
                                </Sidebar>
                            </div>
                    }
                </div>
                <Loading visible={isLoading} />
            </div>
        </>
    )
}

export default ListUser